import React, { useEffect, useRef } from 'react';
import { Avatar, Box, Chip, Typography } from '@mui/material';
import { Mic, MicOff, VideocamOff } from '@mui/icons-material';

const publicationsFor = (participant, kind) => {
  const collection = kind === 'video'
    ? participant?.videoTrackPublications
    : participant?.audioTrackPublications;
  if (!collection) return [];
  return Array.from(collection.values()).filter((publication) => publication?.track);
};

function AttachedTrack({ track, kind, muted = false, audioOutputId = '' }) {
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!track || !element) return undefined;

    let attached = false;
    let cancelled = false;
    const attach = () => {
      if (cancelled) return;
      track.attach(element);
      attached = true;
      if (kind === 'audio') element.play?.().catch(() => {});
    };

    if (kind === 'audio' && audioOutputId && typeof element.setSinkId === 'function') {
      // Select the headphone output before attaching local Cue audio so it
      // cannot briefly leak through the system's default speakers.
      element.setSinkId(audioOutputId).catch(() => {}).then(attach);
    } else {
      attach();
    }

    return () => {
      cancelled = true;
      if (attached) track.detach(element);
    };
  }, [audioOutputId, kind, track]);

  if (kind === 'audio') {
    return <audio ref={elementRef} autoPlay muted={muted} />;
  }

  return (
    <video
      ref={elementRef}
      autoPlay
      playsInline
      muted={muted}
      style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
}

const participantName = (participant, index) =>
  participant?.name || participant?.identity || `Guest ${index + 1}`;

export function ParticipantTile({
  participant,
  index,
  isLocal = false,
  shouldAttachAudio = () => true,
  monitorLocalAudio = false,
  audioOutputId = '',
}) {
  const videoPublication = publicationsFor(participant, 'video')[0] || null;
  const audioPublications = publicationsFor(participant, 'audio');
  const name = participantName(participant, index);
  const microphoneEnabled = participant?.isMicrophoneEnabled !== false;
  const speaking = Boolean(participant?.isSpeaking);

  return (
    <Box
      data-live-studio-participant-tile
      style={{ aspectRatio: '16 / 9' }}
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        borderRadius: 2,
        bgcolor: videoPublication?.track ? 'var(--rs-ink)' : 'var(--rs-surface)',
        border: '1px solid',
        borderColor: speaking ? 'var(--rs-accent)' : 'var(--rs-border)',
        boxShadow: speaking ? '0 0 0 2px var(--rs-live-console-glow)' : 'none',
      }}
    >
      {videoPublication?.track ? (
        <AttachedTrack track={videoPublication.track} kind="video" muted={isLocal} />
      ) : (
        <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 58,
                height: 58,
                mx: 'auto',
                mb: 1,
                bgcolor: 'var(--rs-live-chip-role-bg)',
                color: 'var(--rs-live-chip-role-fg)',
              }}
            >
              {name.slice(0, 1).toUpperCase()}
            </Avatar>
            <VideocamOff sx={{ color: 'text.disabled', fontSize: 18 }} />
          </Box>
        </Box>
      )}

      {((!isLocal && shouldAttachAudio(participant)) || (isLocal && monitorLocalAudio)) &&
        audioPublications.map((publication) => (
        <AttachedTrack
          key={publication.trackSid || publication.track?.sid}
          track={publication.track}
          kind="audio"
          audioOutputId={audioOutputId}
        />
      ))}

      <Box
        sx={{
          position: 'absolute',
          left: 8,
          right: 8,
          bottom: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1,
          py: 0.6,
          borderRadius: 1.5,
          bgcolor: 'var(--rs-overlay-strong)',
          color: 'var(--rs-text)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Typography variant="caption" noWrap sx={{ fontWeight: 700 }}>
          {name}{isLocal ? ' (you)' : ''}
        </Typography>
        {microphoneEnabled ? (
          <Mic sx={{ fontSize: 15, color: speaking ? 'var(--rs-accent)' : 'var(--rs-muted)' }} />
        ) : (
          <MicOff sx={{ fontSize: 15, color: 'var(--rs-live-warning)' }} />
        )}
      </Box>
    </Box>
  );
}

export default function ParticipantMedia({
  participants = [],
  localParticipant,
  shouldAttachAudio = () => true,
  monitorLocalAudio = false,
  audioOutputId = '',
}) {
  if (participants.length === 0) {
    return (
      <Box sx={{ minHeight: 300, display: 'grid', placeItems: 'center', textAlign: 'center', px: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>Studio is ready</Typography>
          <Typography variant="body2" color="text.secondary">
            Start or join a room to see participant video and hear return audio.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        gap: 1,
        height: '100%',
        alignContent: 'start',
      }}
    >
      {participants.slice(0, 7).map((participant, index) => (
        <ParticipantTile
          key={participant.sid || participant.identity || index}
          participant={participant}
          index={index}
          isLocal={participant === localParticipant}
          shouldAttachAudio={shouldAttachAudio}
          monitorLocalAudio={monitorLocalAudio}
          audioOutputId={audioOutputId}
        />
      ))}
      {participants.length > 7 && (
        <Chip label={`+${participants.length - 7} more`} size="small" />
      )}
    </Box>
  );
}
